import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { TextField } from '@mui/material';
import validator from 'validator';

import { AuthenticationStatus, RegistrationStatus } from '@/contracts';
import { registration as actions } from '@/actions';
import { useAppSelector, RootState } from '@/state/store';

import { Button, Container, PasswordField } from '@/components';
import { createSelector } from '@reduxjs/toolkit';
import t from '@/utils/translate';
import isEmailAddressValid from '@/utils/isEmailAddressValid';

type RegistrationFormType = {
  email: string;
  password: string;
  phone: string;
};

const Form = ({
  onSubmit,
  processing = false
}: {
  onSubmit: (data: RegistrationFormType) => void;
  processing?: boolean;
}) => {
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      phone: ''
    },
    validateOnChange: false,
    validateOnBlur: false,
    validate: (values: RegistrationFormType) => {
      const errors: Record<string, string> = {};
      if (!values.email) {
        errors.email = t('registration.validation.noEmail');
      } else if (!isEmailAddressValid(values.email)) {
        errors.email = t('registration.validation.email');
      }

      if (values.phone && !validator.isMobilePhone(values.phone)) {
        errors.phone = t('registration.validation.phone');
      }

      if (!values.password || values.password.length < 6) {
        errors.password = t('registration.validation.pwd.len');
      } else if (!/\d/.test(values.password)) {
        errors.password = t('registration.validation.pwd.dig');
      } else if (!/[a-zа-я]/.test(values.password) || !/[A-ZА-Я]/.test(values.password)) {
        errors.password = t('registration.validation.pwd.reg');
      }

      return errors;
    },
    onSubmit
  });

  return (
    <form onSubmit={formik.handleSubmit} autoComplete="off">
      
      <Container margin-v="small">
        <TextField
          {...formik.getFieldProps('email')}
          autoComplete="off"
          disabled={processing}
          label={t('registration.label.email')}
          error={formik.touched.email && !!formik.errors.email}
          helperText={formik.touched.email && formik.errors.email}
          fullWidth
          margin="dense"
          variant="outlined"
          autoFocus
          placeholder={t('registration.hint.email')}
          size="small"
        />
      </Container>
      {/*
      <Container margin-v="small">
        <PhoneField
          {...formik.getFieldProps('phone')}
          label={t('registration.label.phone')}
          error={formik.touched.phone && !!formik.errors.phone}
          helperText={formik.touched.phone && formik.errors.phone}
          disabled={processing}
          fullWidth
          margin="dense"
          variant="outlined"
          size="small"
        />
      </Container>
      */}
      <Container margin-v="small">
        <PasswordField
          {...formik.getFieldProps('password')}
          autoComplete="new-password"
          disabled={processing}
          label={t('registration.label.pwd')}
          error={formik.touched.password && !!formik.errors.password}
          helperText={formik.touched.password && formik.errors.password}
          fullWidth
          margin="dense"
          variant="outlined"
          placeholder={t('registration.hint.pwd')}
          size="small"
        />
      </Container>

      <Button
        type="submit"
        disabled={processing}
        variant="contained"
        fullWidth
        color="primary"
        size="large"
        loading={processing}
      >
        {t('registration.action.register')}
      </Button>
    </form>
  );
};

type Props = {
  confirmByPhoneUrl: string;
};

export default ({ confirmByPhoneUrl }: Props) => {
  const { status, withPhone, authenticationStatus, exitPoint } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.registration.status,
        (state: RootState) => state.registration.withPhone,
        (state: RootState) => state.authentication.status,
        (state: RootState) => state.common.exitPoint,  
      ],
      (status, withPhone, authenticationStatus, exitPoint) => ({ status, withPhone, authenticationStatus, exitPoint })
    )
  );

  if (![AuthenticationStatus.PENDING, AuthenticationStatus.UNAUTHORIZED].includes(authenticationStatus)) {
    return <Navigate to={exitPoint || '/'} replace/>;
  }

  if (withPhone && (status === RegistrationStatus.CONFIRMATION)) {
    return <Navigate to={confirmByPhoneUrl} replace/>;
  }

  return (
    <>
      <h3>{t('registration.title.main', true)}</h3>
      { status === RegistrationStatus.CONFIRMATION ? (
        <p>
          {t('registration.message.emailSent', true)}
        </p>
      ) : (
        <Form
          processing={status === RegistrationStatus.PROCESSING}
          onSubmit={(data: RegistrationFormType) => {
            actions.registration({
              email: data.email,
              password: data.password,
              ...(data.phone ? { phone: data.phone } : {})
            })
          }}
        />
      )}
    </>
  );
};
